import { useEffect, useState } from "react";
import { Button, Empty, Spin, message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "@layouts/PageLayout";
import { addToCart } from "@store/cartSlice";
import { useAppDispatch } from "@store/hooks";
import { formatInr, getProduct } from "@services/shop.service";
import "./shop.css";

const fallbackImage = "https://placehold.co/800x600?text=Product";

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getProduct(id)
      ?.then((data) => setProduct(data?.id ? data : null))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const addProduct = async () => {
    if (!product || product.stock < 1) return;
    setAdding(true);
    try {
      await dispatch(addToCart({ product_id: product.id, quantity: 1 })).unwrap();
      message.success("Added to cart");
      navigate("/cart");
    } catch (error) {
      message.error("Could not add this product");
    } finally {
      setAdding(false);
    }
  };

  return (
    <PageLayout>
      <div className="shop-page">
        <Button type="link" style={{ paddingLeft: 0, marginBottom: 8 }} onClick={() => navigate("/")}>
          Back to shop
        </Button>
        {loading ? (
          <div className="surface" style={{ textAlign: "center", padding: 48 }}><Spin /></div>
        ) : !product ? (
          <div className="surface"><Empty description="This product is no longer available" /></div>
        ) : (
          <div className="detail-layout">
            <img
              src={product.image_url || fallbackImage}
              alt={product.name}
              onError={(event) => { event.currentTarget.src = fallbackImage; }}
            />
            <div className="detail-copy">
              <div className="muted">{product.category || "General"} · {product.sku}</div>
              <h2>{product.name}</h2>
              <div className="price-row">
                <strong>{formatInr(product.price)}</strong>
                {Number(product.compare_at_price) > Number(product.price) ? (
                  <span className="compare">{formatInr(product.compare_at_price)}</span>
                ) : null}
              </div>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{product.description || "No description yet."}</p>
              <p className="muted" style={{ margin: 0 }}>
                {product.stock > 0 ? `${product.stock} available` : "Out of stock"}
                {product.rating ? ` · ${product.rating}` : ""}
              </p>
              <div className="actions">
                <Button type="primary" loading={adding} disabled={product.stock < 1} onClick={addProduct}>
                  Add to cart
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default ProductPage;
